export interface PasswordSecurityRating {
  strength: 'low' | 'medium' | 'strong';
  score: number;
  issues: string[];
  recommendations: string[];
}

export interface SecurityOverview {
  totalPasswords: number;
  lowCount: number;
  mediumCount: number;
  strongCount: number;
  overallScore: number;
  issues: string[];
  recommendations: string[];
}

export class PasswordSecurityService {
  /**
   * Calculate comprehensive password strength with detailed scoring
   */
  static calculatePasswordStrength(password: string): PasswordSecurityRating {
    if (!password || password.length === 0) {
      return {
        strength: 'low',
        score: 0,
        issues: ['Password is empty'],
        recommendations: ['Create a password with at least 8 characters'],
      };
    }

    let score = 0;
    const issues: string[] = [];
    const recommendations: string[] = [];

    // Length checks
    if (password.length < 8) {
      issues.push('Password is too short (minimum 8 characters)');
      recommendations.push('Increase password length to at least 12 characters');
    } else if (password.length >= 8 && password.length < 12) {
      score += 1;
      recommendations.push('Consider using 12+ characters for better security');
    } else if (password.length >= 12 && password.length < 16) {
      score += 2;
    } else if (password.length >= 16) {
      score += 3;
    }

    // Character variety checks
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    if (!hasLowercase) {
      issues.push('Missing lowercase letters');
      recommendations.push('Add lowercase letters (a-z)');
    } else {
      score += 1;
    }

    if (!hasUppercase) {
      issues.push('Missing uppercase letters');
      recommendations.push('Add uppercase letters (A-Z)');
    } else {
      score += 1;
    }

    if (!hasNumbers) {
      issues.push('Missing numbers');
      recommendations.push('Add numbers (0-9)');
    } else {
      score += 1;
    }

    if (!hasSpecialChars) {
      issues.push('Missing special characters');
      recommendations.push('Add special characters (!@#$%^&*)');
    } else {
      score += 1;
    }

    // Pattern checks (common weak patterns)
    if (this.hasCommonPatterns(password)) {
      issues.push('Contains common patterns (e.g., 123, abc, qwerty)');
      recommendations.push('Avoid predictable patterns');
      score = Math.round(score * 0.7); // Reduce score for patterns
    }

    // Repetition checks
    if (this.hasExcessiveRepetition(password)) {
      issues.push('Contains repeated characters');
      recommendations.push('Avoid repeating the same character multiple times');
      score = Math.round(score * 0.8);
    }

    // Sequential characters check
    if (this.hasSequentialChars(password)) {
      issues.push('Contains sequential characters (e.g., abc, 123)');
      recommendations.push('Avoid sequential patterns');
      score = Math.round(score * 0.75);
    }

    // Dictionary/common word check
    if (this.isCommonPassword(password)) {
      issues.push('Password appears to be a common word');
      recommendations.push('Use a unique password, not a dictionary word');
      score = Math.round(score * 0.5);
    }

    // Determine strength level
    let strength: 'low' | 'medium' | 'strong';
    if (score <= 3) {
      strength = 'low';
    } else if (score <= 6) {
      strength = 'medium';
    } else {
      strength = 'strong';
    }

    // Add positive feedback
    if (score >= 6) {
      recommendations.unshift('Great password! Consider enabling 2FA for extra security');
    }

    return {
      strength,
      score,
      issues,
      recommendations,
    };
  }

  private static hasCommonPatterns(password: string): boolean {
    const commonPatterns = [
      '123',
      'abc',
      'qwerty',
      'password',
      'admin',
      'letmein',
      'welcome',
      'monkey',
      'dragon',
    ];

    const lowerPassword = password.toLowerCase();
    return commonPatterns.some((pattern) => lowerPassword.includes(pattern));
  }

  private static hasExcessiveRepetition(password: string): boolean {
    // Check for 3+ consecutive same characters
    for (let i = 0; i < password.length - 2; i++) {
      if (password[i] === password[i + 1] && password[i] === password[i + 2]) {
        return true;
      }
    }
    return false;
  }

  private static hasSequentialChars(password: string): boolean {
    // Check for sequential characters (abc, 123, etc.)
    for (let i = 0; i < password.length - 2; i++) {
      const char1 = password.charCodeAt(i);
      const char2 = password.charCodeAt(i + 1);
      const char3 = password.charCodeAt(i + 2);

      // Check forward sequence
      if (char2 === char1 + 1 && char3 === char2 + 1) {
        return true;
      }
      // Check backward sequence
      if (char2 === char1 - 1 && char3 === char2 - 1) {
        return true;
      }
    }
    return false;
  }

  private static isCommonPassword(password: string): boolean {
    const commonPasswords = [
      'password',
      '123456',
      '12345678',
      'qwerty',
      'abc123',
      'password1',
      'admin',
      'letmein',
      'welcome',
      'monkey',
      '1234567',
      '123456789',
      '1234567890',
      'iloveyou',
      'princess',
      'rockyou',
      '123qwe',
    ];

    return commonPasswords.includes(password.toLowerCase());
  }

  /**
   * Calculate overall security score for all passwords
   */
  static calculateSecurityOverview(
    passwords: Array<{
      password_encrypted?: string;
      encrypted_password?: string;
      password?: string;
      strength?: string;
    }>
  ): SecurityOverview {
    if (passwords.length === 0) {
      return {
        totalPasswords: 0,
        lowCount: 0,
        mediumCount: 0,
        strongCount: 0,
        overallScore: 0,
        issues: ['No passwords found'],
        recommendations: ['Start adding passwords to improve your security'],
      };
    }

    let lowCount = 0;
    let mediumCount = 0;
    let strongCount = 0;
    const allIssues: string[] = [];
    const allRecommendations: string[] = [];

    for (const password of passwords) {
      const encryptedPassword =
        password.password_encrypted ||
        password.encrypted_password ||
        password.password ||
        '';

      // If we can't decrypt or password is too short, use stored strength
      if (!encryptedPassword || encryptedPassword.length < 3) {
        const storedStrength = (password.strength || 'medium').toLowerCase();
        // Map old strength values to new simplified ones
        if (storedStrength === 'weak' || storedStrength === 'low') {
          lowCount++;
        } else if (storedStrength === 'medium') {
          mediumCount++;
        } else if (storedStrength === 'strong' || storedStrength === 'very_strong') {
          strongCount++;
        } else {
          mediumCount++; // Default to medium
        }
        continue;
      }

      // Calculate strength (in production, you'd decrypt first)
      // For now, we'll use the encrypted length as a proxy
      // In a real implementation, you'd decrypt the password first
      const rating = this.calculatePasswordStrength(encryptedPassword.toString());

      switch (rating.strength) {
        case 'low':
          lowCount++;
          break;
        case 'medium':
          mediumCount++;
          break;
        case 'strong':
          strongCount++;
          break;
        default:
          mediumCount++; // Default to medium
      }

      allIssues.push(...rating.issues);
      allRecommendations.push(...rating.recommendations);
    }

    // Calculate overall score (0-100)
    const total = passwords.length;
    const lowPenalty = (lowCount / total) * 50;
    const mediumPenalty = (mediumCount / total) * 20;
    const strongBonus = (strongCount / total) * 30;

    const overallScore = Math.round(
      Math.max(0, Math.min(100, 100 - lowPenalty - mediumPenalty + strongBonus))
    );

    // Remove duplicate issues and recommendations
    const uniqueIssues = Array.from(new Set(allIssues)).slice(0, 5); // Top 5 issues
    const uniqueRecommendations = Array.from(new Set(allRecommendations)).slice(0, 5); // Top 5 recommendations

    return {
      totalPasswords: total,
      lowCount,
      mediumCount,
      strongCount,
      overallScore,
      issues: uniqueIssues,
      recommendations: uniqueRecommendations,
    };
  }

  static getOverallRating(score: number): string {
    if (score >= 70) return 'Excellent';
    if (score >= 50) return 'Good';
    return 'Needs Improvement';
  }
}

